import { Link, useSearchParams } from "react-router-dom";

// T12.a / T54: skeleton cards with the SAME anatomy as ProductCard (12px-padded square tile; body: brand 16, 2-line title, price 28, pill 24)
export const SkeletonGrid = ({ n = 6, cols = "grid-cols-2 xl:grid-cols-3", testid = "grid-skeleton" }) => (
  <div className={`grid ${cols} gap-4`} data-testid={testid} aria-hidden="true">
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="p-3 pb-0"><div className="aspect-square animate-pulse rounded-lg border border-slate-100 bg-slate-100 motion-reduce:animate-none" /></div>
        <div className="flex flex-1 flex-col gap-2 p-4">
          <div className="h-4 w-1/3 rounded bg-slate-100" />
          <div className="h-[39px] flex-1 text-sm"><div className="h-4 rounded bg-slate-100" /><div className="mt-1.5 h-4 w-2/3 rounded bg-slate-100" /></div>
          <div className="mt-1 h-7 w-1/2 rounded bg-slate-100" />
          <div className="h-6 w-2/5 rounded-full bg-slate-100" />
        </div>
      </div>))}
  </div>
);

function pages(cur, last) {
  const set = new Set([1, last, cur - 1, cur, cur + 1].filter((n) => n >= 1 && n <= last));
  const arr = [...set].sort((a, b) => a - b);
  const out = [];
  arr.forEach((n, i) => { if (i && n - arr[i - 1] > 1) out.push("…" + n); out.push(n); });
  return out;
}

// T12.a simple numbered pagination (?page=N, keeps every other query param); restyled in Phase 3
export function Pagination({ page, totalPages, total, testid = "pagination" }) {
  const [sp] = useSearchParams();
  if (!totalPages || totalPages <= 1) return null;
  const href = (n) => { const q = new URLSearchParams(sp); q.delete("cursor"); if (n === 1) q.delete("page"); else q.set("page", n); const s = q.toString(); return s ? `?${s}` : "?"; };
  const cls = "min-w-9 h-9 px-3 inline-flex items-center justify-center rounded-lg border text-sm font-medium";
  return (
    <nav className="flex flex-wrap items-center gap-2 mt-8" aria-label="Pagination" data-testid={testid}>
      {page > 1 && <Link to={href(page - 1)} rel="prev" data-testid={`${testid}-prev`} className={`${cls} border-slate-300 bg-white hover:border-[var(--brand-primary)]`}>Previous</Link>}
      {pages(page, totalPages).map((n) => typeof n === "string"
        ? <span key={n} className="px-1 text-slate-400">…</span>
        : <Link key={n} to={href(n)} aria-current={n === page ? "page" : undefined} data-testid={`${testid}-page-${n}`}
            className={`${cls} ${n === page ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-slate-300 bg-white hover:border-[var(--brand-primary)]"}`}>{n}</Link>)}
      {page < totalPages && <Link to={href(page + 1)} rel="next" data-testid={`${testid}-next`} className={`${cls} border-slate-300 bg-white hover:border-[var(--brand-primary)]`}>Next</Link>}
      <span className="text-xs text-slate-500 ml-2" data-testid={`${testid}-summary`}>Page {page} of {totalPages.toLocaleString()} · {total.toLocaleString()} products</span>
    </nav>
  );
}

// keep SEO tags right for paged listing URLs (self-canonical incl. ?page=N, prev/next links)
export function setPagedHead(path, page, totalPages, robots) {
  const set = (sel, make, attr, val) => { let el = document.querySelector(sel); if (val == null) { if (el) el.remove(); return; } if (!el) { el = make(); document.head.appendChild(el); } el[attr] = val; };
  const link = (rel) => () => { const l = document.createElement("link"); l.rel = rel; return l; };
  const base = path.replace(/[?&]+$/, "");
  const url = (n) => `${window.location.origin}${base}${n > 1 ? `${base.includes("?") ? "&" : "?"}page=${n}` : ""}`;
  set('link[rel="canonical"]', link("canonical"), "href", url(page));
  set('link[rel="prev"]', link("prev"), "href", page > 1 ? url(page - 1) : null);
  set('link[rel="next"]', link("next"), "href", page < totalPages ? url(page + 1) : null);
  if (robots) set('meta[name="robots"]', () => { const m = document.createElement("meta"); m.name = "robots"; return m; }, "content", robots);
}
